import React from 'react';
import DetailsPortal from './DetailsPortal';
import './OrderDetailsPanel.css';

const getRoleFieldVisibility = (role) => {
  const normalizedRole = String(role || '').toLowerCase();
  return {
    showBalance: normalizedRole === 'trader',
    showGatewayId: normalizedRole === 'gateway',
    showTraderFields: normalizedRole === 'trader'
  };
};

const TraderDetailsPanel = ({ trader, onClose, onEdit }) => {
  if (!trader) return null;

  const traderData = trader;

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp);
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  const getStatus = () => {
    const raw = traderData.status || traderData.Status || traderData.STATUS || '-';
    const lower = String(raw).toLowerCase();
    return {
      status: lower,
      text: raw === '-' ? raw : String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase()
    };
  };

  const status = getStatus();
  const ref = traderData.ref || traderData.id || '-';
  const login = traderData.login || traderData.Login || traderData.LOGIN || '-';
  const name = traderData.name || traderData.Name || traderData.NAME || '-';
  const role = traderData.role || traderData.Role || traderData.ROLE || '-';
  const gatewayId = traderData.gatewayId || traderData.GatewayId || traderData.GATEWAY_ID || traderData.id || traderData.Id || traderData.ID || '-';
  const wallet = traderData.wallet || traderData.Wallet || traderData.WALLET || '-';
  const markupCoefficient = traderData.markupCoefficient ?? traderData.MarkupCoefficient ?? traderData.MARKUP_COEFFICIENT ?? '-';
  const minFiatPayIn = traderData.minFiatPayIn || traderData.MinFiatPayIn || traderData.MIN_FIAT_PAYIN || '-';
  const maxFiatPayIn = traderData.maxFiatPayIn || traderData.MaxFiatPayIn || traderData.MAX_FIAT_PAYIN || '-';
  const minFiatPayOut = traderData.minFiatPayOut || traderData.MinFiatPayOut || traderData.MIN_FIAT_PAYOUT || '-';
  const maxFiatPayOut = traderData.maxFiatPayOut || traderData.MaxFiatPayOut || traderData.MAX_FIAT_PAYOUT || '-';
  const created = formatDate(traderData.create_time || traderData.Created || traderData.created);
  const balance = traderData.balance || traderData.Balance || traderData.BALANCE || null;
  const currentBalance =
    typeof balance === 'object' && balance
      ? balance.current || balance.Current || balance.CURRENT || '-'
      : '-';
  const pendingBalance =
    typeof balance === 'object' && balance
      ? balance.pending || balance.Pending || balance.PENDING || '-'
      : '-';
  const totalBalance =
    typeof balance === 'object' && balance
      ? balance.total || balance.Total || balance.TOTAL || '-'
      : '-';
  const { showBalance, showGatewayId, showTraderFields } = getRoleFieldVisibility(role);

  return (
    <DetailsPortal>
    <div className="order-details-overlay" onClick={onClose}>
      <div className="order-details-panel" onClick={(e) => e.stopPropagation()}>
        <div className="order-details-header">
          <h2>Trader Details</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onEdit && (
              <button 
                className="edit-button"
                onClick={() => {
                  if (onEdit) {
                    onEdit(trader);
                  }
                }}
              >
                Edit
              </button>
            )}
            <button type="button" className="close-button" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="order-details-content">
          <div className="order-summary">
            <div className="summary-main">
              <span className="summary-label">Trader</span>
              <span className="summary-primary">{formatValue(name)}</span>
              <span className="summary-secondary">{formatValue(login)}</span>
            </div>
            <div className="summary-chips">
              <span className={`status-badge status-${status.status}`}>{status.text}</span>
              <span className="summary-chip">Role: {formatValue(role)}</span>
              <span className="summary-chip">Current: {formatValue(currentBalance)} USDT</span>
            </div>
          </div>

          <div className="detail-section">
            <h3>Profile</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Ref</span>
                <span className="detail-value">{formatValue(ref)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Login</span>
                <span className="detail-value">{formatValue(login)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Name</span>
                <span className="detail-value">{formatValue(name)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Role</span>
                <span className="detail-value">{formatValue(role)}</span>
              </div>
              {showGatewayId && (
                <div className="detail-item">
                  <span className="detail-label">Gateway ID</span>
                  <span className="detail-value">{formatValue(gatewayId)}</span>
                </div>
              )}
              {showTraderFields && (
                <div className="detail-item">
                  <span className="detail-label">Wallet</span>
                  <span className="detail-value">{formatValue(wallet)}</span>
                </div>
              )}
            </div>
          </div>

          {showBalance && (
            <div className="detail-section">
              <h3>Balance</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Current</span>
                  <span className="detail-value">{formatValue(currentBalance)} USDT</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pending</span>
                  <span className="detail-value">{formatValue(pendingBalance)} USDT</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total</span>
                  <span className="detail-value">{formatValue(totalBalance)} USDT</span>
                </div>
              </div>
            </div>
          )}

          {showTraderFields && (
            <div className="detail-section">
              <h3>Coefficients</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Markup Coefficient</span>
                  <span className="detail-value">{formatValue(markupCoefficient)}</span>
                </div>
              </div>
            </div>
          )}

          {showTraderFields && (
            <div className="detail-section">
              <h3>Limits</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Min Fiat Pay In</span>
                  <span className="detail-value">{formatValue(minFiatPayIn)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Max Fiat Pay In</span>
                  <span className="detail-value">{formatValue(maxFiatPayIn)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Min Fiat Pay Out</span>
                  <span className="detail-value">{formatValue(minFiatPayOut)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Max Fiat Pay Out</span>
                  <span className="detail-value">{formatValue(maxFiatPayOut)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Timeline</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Created</span>
                <span className="detail-value">{created}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </DetailsPortal>
  );
};

export default TraderDetailsPanel;
